/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import { Box, Flex } from "rebass";
import styled from "styled-components";
import { EventInfo } from "../../../components/EventInfo";
import { PLANNED } from "../../../components/EventType";
import { ETRRange } from "../../../components/ETRRange";
import { OutageReason } from "../../../components/OutageReason";
import { HOLDING_MESSAGE_TYPE } from "../../../constants/holdingMessageType";
import { Button } from "../../../components/Button";
import { PreliminaryEtrLearnMoreLink } from "../../../components/PreliminaryEtrLearnMoreLink/PreliminaryEtrLearnMoreLink";
import { InlineMessage } from "../../../components/InlineMessage/InlineMessage";

const OUTAGE_CATEGORY_MESSAGE = {
  preliminaryOutageCategoryMessage: `
    Based on initial assessment – may change if conditions differ on site.
  `,
  tbaOutageCategoryMessage: `
    Our crews are onsite investigating a complex fault. We’ll provide an estimated restoration time once assessments are complete.
  `,
};

const OutageCategoryMessageContainer = styled.span`
  margin-top: 8px;
  color: ${props => props.theme.colors.lightGray};
  font-size: ${props => props.theme.fontSizes[1]}px;
`;

export const OutageInfo = ({
  subscription,
  isResolved,
  unPlannedResolveText,
  outage: {
    etrStart,
    etrEnd,
    eventType,
    outageId,
    outageStart,
    outageReasonDescription,
    holdingMessage,
    preliminaryEtrType,
    etrStatus,
  },
  workStatus,
}) => {
  let label = unPlannedResolveText;
  let startTime = etrStart;

  const isEtrStatusUninitiated =
    etrStatus == null ||
    (typeof etrStatus === "string" &&
      etrStatus.toLowerCase() === "uninitiated");
  const isEtrStatusTBA = typeof etrStatus === "string" && etrStatus === "TBA";
  const isEtrStatusPreliminary =
    typeof etrStatus === "string" && etrStatus.toLowerCase() === "preliminary";
  const isEtrStatusActual =
    typeof etrStatus === "string" && etrStatus.toLowerCase() === "actual";
  const isPlanned =
    typeof eventType === "string" && eventType.toLowerCase() === "planned";
  const showOutageCategoryMessage =
    isEtrStatusUninitiated && !holdingMessage && !isPlanned;

  if (eventType === PLANNED) {
    label = "OUTAGE TIME(S)";
    startTime = outageStart;
  }
  if (isResolved) {
    label = "OUTAGE RESOLVED";
  }

  const scrollToLatestMessage = () => {
    const target = document.getElementById("latestEventItem");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  const renderEtrRange = () => <ETRRange start={startTime} end={etrEnd} />;

  const renderOutageCategoryMessage = () => (
    <OutageCategoryMessageContainer>
      {isEtrStatusTBA
        ? OUTAGE_CATEGORY_MESSAGE.tbaOutageCategoryMessage
        : OUTAGE_CATEGORY_MESSAGE.preliminaryOutageCategoryMessage}
    </OutageCategoryMessageContainer>
  );

  const renderToBeConfirmedMessage = () => "to be confirmed";

  const renderHoldingMessage = () => (
    <Button
      variant="text"
      textAlign="left"
      style={{ padding: 0 }}
      onClick={scrollToLatestMessage}
      data-testid="OutageInfo__EstimatedRestorationMessage"
    >
      {holdingMessage.shortMessage.toLowerCase()}
    </Button>
  );

  const renderOutageShortMessage = () => {
    if (!isResolved) {
      if (isPlanned) {
        if (etrEnd) {
          return (
            <>
              <span data-testid="OutageInfo__ETRRange_Planned">
                {renderEtrRange()}
              </span>
            </>
          );
        }

        if (holdingMessage) {
          return (
            <>
              <span data-testid="OutageInfo__HoldingMessage_Planned">
                {renderHoldingMessage()}
              </span>
            </>
          );
        }
      } else {
        if (isEtrStatusActual) {
          return (
            <>
              <span data-testid="OutageInfo__ETRRange_Unplanned">
                {renderEtrRange()}
              </span>
            </>
          );
        }

        if (holdingMessage) {
          return (
            <>
              <span data-testid="OutageInfo__HoldingMessage_Planned">
                {renderHoldingMessage()}
              </span>
            </>
          );
        }

        if (isEtrStatusPreliminary && etrEnd) {
          return (
            <>
              <span data-testid="OutageInfo__ETRRange_Unplanned">
                {renderEtrRange()}
              </span>
              <br />
              <span data-testid="OutageInfo__OutageCategoryMessage_Unplanned">
                {renderOutageCategoryMessage()}
              </span>
            </>
          );
        }

        if (isEtrStatusTBA) {
          return (
            <>
              <span data-testid="OutageInfo__EstimatedRestorationMessage_Unplanned">
                {renderToBeConfirmedMessage()}
              </span>
              <br />
              <span data-testid="OutageInfo__OutageCategoryMessage_Unplanned">
                {renderOutageCategoryMessage()}
              </span>
            </>
          );
        }

        if (isEtrStatusUninitiated) {
          return (
            <>
              <span data-testid="OutageInfo__EstimatedRestorationMessage_Unplanned">
                {renderToBeConfirmedMessage()}
              </span>
            </>
          );
        }
      }

      return (
        <span data-testid="OutageInfo__EstimatedRestorationMessage">
          {renderToBeConfirmedMessage()}
        </span>
      );
    }
    return <></>;
  };

  return (
    <Flex
      justifyContent={["space-between", null, "start"]}
      mb={10}
      flexDirection="column"
    >
      <InlineMessage
        showOutageCategoryMessage={showOutageCategoryMessage}
        content={
          <>
            About <strong>75% of outages</strong> are fixed{" "}
            <strong>within four hours</strong> from the report time, in the
            absence of severe weather events. We&apos;ll provide the estimated
            restoration time after arrival.
          </>
        }
        preliminaryEtrType={preliminaryEtrType}
      />

      <Box py={[null, null, 10]} pr={[20, null, 25]} width={1} mt={3}>
        <EventInfo label={label} mb={2} fontWeight="bold">
          {renderOutageShortMessage()}
        </EventInfo>
      </Box>

      <OutageReason
        subscription={subscription}
        outage={{ eventType, outageId, outageReasonDescription, workStatus }}
      />

      {!isPlanned && (
        <PreliminaryEtrLearnMoreLink
          preliminaryEtrType={preliminaryEtrType}
          testIdPrefix="OutageInfo"
        />
      )}
    </Flex>
  );
};

OutageInfo.propTypes = {
  subscription: PropTypes.shape({
    ticketId: PropTypes.string,
  }).isRequired,
  isResolved: PropTypes.bool.isRequired,
  unPlannedResolveText: PropTypes.string.isRequired,
  outage: PropTypes.shape({
    etrStart: PropTypes.string,
    etrEnd: PropTypes.string,
    eventType: PropTypes.string,
    outageId: PropTypes.string,
    holdingMessage: PropTypes.shape({
      type: PropTypes.oneOf(Object.values(HOLDING_MESSAGE_TYPE)),
      shortMessage: PropTypes.string,
      longMessage: PropTypes.string,
    }),
    preliminaryEtrType: PropTypes.string,
    etrStatus: PropTypes.string,
  }).isRequired,
  workStatus: PropTypes.shape({
    label: PropTypes.oneOf([
      "Outage reported",
      "On our way",
      "Investigating",
      "Resolving",
      "Outage resolved",
    ]),
    type: PropTypes.oneOf([
      "REPORTED",
      "ENROUTE",
      "INVESTIGATING",
      "WORKING",
      "RESOLVED",
    ]),
    active: PropTypes.bool,
    complete: PropTypes.bool,
  }).isRequired,
};
